export const STUDY_NAMESPACE = "chi2027-2x2-prototype-v0.5";
export const PROTOCOL_VERSION = "chi2027-2x2-v0.5";
export const CARD_VERSION = "verification-card-v0.3";
export const NOT_REVIEWED = "NOT_REVIEWED";
export const NO_MISMATCH_ACTION = "NONE";
export const EDIT_VALUE = "EDIT_VALUE";
export const FLAG_FOR_FINAL_REVIEW = "FLAG_FOR_FINAL_REVIEW";

export const REVIEW_STATUS_OPTIONS = Object.freeze([
  Object.freeze({ value: "MATCHES_SOURCE", label: "Matches scenario" }),
  Object.freeze({ value: "DOES_NOT_MATCH_SOURCE", label: "Does not match scenario" }),
  Object.freeze({ value: "CANNOT_DETERMINE", label: "Cannot determine from scenario" }),
]);

export const REVIEW_STATUS_VALUES = Object.freeze(
  REVIEW_STATUS_OPTIONS.map((option) => option.value)
);

export const MISMATCH_ACTION_VALUES = Object.freeze([
  EDIT_VALUE,
  FLAG_FOR_FINAL_REVIEW,
]);

const SHARED_CARD = Object.freeze({
  version: CARD_VERSION,
  prompt: "Does the AI-proposed value match the scenario?",
  initial_status: NOT_REVIEWED,
  status_options: REVIEW_STATUS_OPTIONS,
  mismatch_dialog_prompt: "The AI-proposed value was marked as not matching the scenario. What would you like to do?",
  mismatch_actions: Object.freeze([
    Object.freeze({ value: EDIT_VALUE, label: "Edit value" }),
    Object.freeze({ value: FLAG_FOR_FINAL_REVIEW, label: "Flag for final review" }),
  ]),
  edit_action_label: "Edit value",
  flag_action_label: "Flag for final review",
  mismatch_edited_copy: Object.freeze([
    "Original proposal marked as not matching",
    "Report value edited",
  ]),
  mismatch_flagged_copy: "Marked as not matching — value not edited",
  unclear_copy: "Marked as unclear",
  view_full_scenario_label: "View full scenario",
  required_validation_copy: "Select a review status before continuing.",
});

export const RESPONSE_REQUIREMENT_CUES = Object.freeze({
  OPTIONAL: Object.freeze({
    label: "Review response: Optional",
    text: "You can continue without selecting a review status.",
  }),
  REQUIRED: Object.freeze({
    label: "Review response: Required",
    text: "Select a review status to continue.",
  }),
});

export const CONDITION_DEFINITIONS = Object.freeze({
  INSTANT_OPT: Object.freeze({
    verification_schedule: "INSTANT",
    response_requirement: "OPTIONAL",
    progression_cue: RESPONSE_REQUIREMENT_CUES.OPTIONAL,
    verification_card: SHARED_CARD,
  }),
  INSTANT_REQ: Object.freeze({
    verification_schedule: "INSTANT",
    response_requirement: "REQUIRED",
    progression_cue: RESPONSE_REQUIREMENT_CUES.REQUIRED,
    verification_card: SHARED_CARD,
  }),
  FINAL_OPT: Object.freeze({
    verification_schedule: "FINAL",
    response_requirement: "OPTIONAL",
    progression_cue: RESPONSE_REQUIREMENT_CUES.OPTIONAL,
    verification_card: SHARED_CARD,
  }),
  FINAL_REQ: Object.freeze({
    verification_schedule: "FINAL",
    response_requirement: "REQUIRED",
    progression_cue: RESPONSE_REQUIREMENT_CUES.REQUIRED,
    verification_card: SHARED_CARD,
  }),
});

export const CONDITION_CODES = Object.freeze(Object.keys(CONDITION_DEFINITIONS));

function remoteCueMatches(remoteCue, localCue) {
  return remoteCue?.label === localCue.label && remoteCue?.text === localCue.text;
}

function remoteCardMatches(remoteCard) {
  if (!remoteCard || typeof remoteCard !== "object") return false;
  const remoteOptions = Array.isArray(remoteCard.status_options)
    ? remoteCard.status_options.map((option) => `${option?.value}:${option?.label}`)
    : [];
  const localOptions = REVIEW_STATUS_OPTIONS.map(
    (option) => `${option.value}:${option.label}`
  );
  const remoteMismatchActions = Array.isArray(remoteCard.mismatch_actions)
    ? remoteCard.mismatch_actions.map((option) => `${option?.value}:${option?.label}`)
    : [];
  const localMismatchActions = SHARED_CARD.mismatch_actions.map(
    (option) => `${option.value}:${option.label}`
  );
  return remoteCard.version === SHARED_CARD.version
    && remoteCard.prompt === SHARED_CARD.prompt
    && remoteCard.initial_status === SHARED_CARD.initial_status
    && remoteCard.mismatch_dialog_prompt === SHARED_CARD.mismatch_dialog_prompt
    && JSON.stringify(remoteMismatchActions) === JSON.stringify(localMismatchActions)
    && remoteCard.edit_action_label === SHARED_CARD.edit_action_label
    && remoteCard.flag_action_label === SHARED_CARD.flag_action_label
    && JSON.stringify(remoteCard.mismatch_edited_copy) === JSON.stringify(SHARED_CARD.mismatch_edited_copy)
    && remoteCard.mismatch_flagged_copy === SHARED_CARD.mismatch_flagged_copy
    && remoteCard.unclear_copy === SHARED_CARD.unclear_copy
    && remoteCard.view_full_scenario_label === SHARED_CARD.view_full_scenario_label
    && remoteCard.required_validation_copy === SHARED_CARD.required_validation_copy
    && JSON.stringify(remoteOptions) === JSON.stringify(localOptions);
}

export function conditionDefinition(code, remoteConfig = null, remoteCard = null) {
  const normalized = String(code || "").toUpperCase();
  const local = CONDITION_DEFINITIONS[normalized];
  if (!local) throw new Error(`Unknown study condition: ${normalized || "<empty>"}`);

  if (remoteConfig && typeof remoteConfig === "object") {
    const remoteSchedule = String(remoteConfig.verification_schedule || "").toUpperCase();
    const remoteRequirement = String(remoteConfig.response_requirement || "").toUpperCase();
    if (
      remoteSchedule !== local.verification_schedule
      || remoteRequirement !== local.response_requirement
      || !remoteCueMatches(remoteConfig.progression_cue, local.progression_cue)
      || !remoteCardMatches(remoteCard)
    ) {
      throw new Error(`Condition contract mismatch for ${normalized}`);
    }
  }
  return local;
}

export function isSubstantiveReviewStatus(value) {
  return REVIEW_STATUS_VALUES.includes(String(value || "").toUpperCase());
}

export function isMismatchAction(value) {
  return MISMATCH_ACTION_VALUES.includes(String(value || "").toUpperCase());
}
